import React, { useState, useEffect } from "react";
import useUserData from "../../hooks/useUserData";
import { useNavigate } from "react-router-dom";
import {
  logout,
  storeUserData,
  verifyMfaLogin,
  startOrgEntryMfaChallenge,
} from "../../services/apiService";
import { changeOrganizationSession } from "../../services/orgSwitchService";
import { clearOrgModulesHydrationAttempt } from "../../services/orgModulesHydrationService";
import { clearOrgPlatformFlagsHydrationAttempt } from "../../services/orgPlatformFlagsHydrationService";
import { setAuthItem } from "../../services/authSessionStore";
import "./OrganizationSelector.scss";
import { resolvePostLoginRoute } from "../../utils/credentialGateUtils";
import {
  getAllPermissionKeys,
  isPlatformOrSystemAdminRole,
  stampPlatformSuperAdminSession,
  isSessionSuperAdmin,
} from "../../utils/permissionUtils";
import {
  applySponsorCroLoginSession,
  resolveOtherOrgLinkType,
} from "../../utils/sponsorCroAccess";
import { isSponsorCroUserRecord } from "../../constants/userTypeLabels";
import MfaVerification from "./MfaVerification";
import MfaMethodChoice from "./MfaMethodChoice";
import MfaLoginAuthenticatorSetup from "./MfaLoginAuthenticatorSetup";

function isOrgMfaRequired(value) {
  if (value === false || value === 0 || value === "0") return false;
  if (typeof value === "string" && value.trim().toLowerCase() === "false") {
    return false;
  }
  return true;
}

const OrganizationSelector = () => {
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState([]);
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [switchingOrgId, setSwitchingOrgId] = useState(null);
  const [switchError, setSwitchError] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaStep, setMfaStep] = useState(null); // mfaChoose | mfaAuthSetup | mfa
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [pendingOrg, setPendingOrg] = useState(null);
  const [mfaVerifiedToken, setMfaVerifiedToken] = useState("");
  const { userData, refreshUserData } = useUserData();

  const handleLogout = () => {
    logout(navigate);
  };

  const [selectedOrg, setSelectedOrg] = useState(null);

  useEffect(() => {
    if (!userData?.OrganizationId || !userData?.StaffId) {
      return;
    }
    const orgLinks = Array.isArray(userData.OtherOrgLink)
      ? userData.OtherOrgLink
      : userData.OtherOrgLink?.Table || [];
    const mockOrganizations = orgLinks.map((item) => ({
      id: item.OrganizationId ?? item.organizationId,
      name: item.OrgName ?? item.orgName,
      role: item.Roles ?? item.roles ?? item.Role ?? item.role,
      memberCount: 0,
      type: resolveOtherOrgLinkType(item),
      staffId: item.StaffId ?? item.staffId,
      mfaEnabled: isOrgMfaRequired(item.MfaEnabled ?? item.mfaEnabled),
    }));
    setTimeout(() => {
      setOrganizations(mockOrganizations);
      setFilteredOrgs(mockOrganizations);
      setLoading(false);
    }, 300);
  }, [userData]);

  useEffect(() => {
    const filtered = organizations.filter((org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredOrgs(filtered);
  }, [searchTerm, organizations]);

  const applyOrgSession = (session, org) => {
    const role = session.Role || session.Roles || org.role || "";
    const sessionIsCro =
      isSponsorCroUserRecord(
        session.IsCRO ?? session.isCRO ?? session.IsCro,
      ) || org.type === "CRO";
    const croIsCroValue = Number(
      session.IsCRO ?? session.isCRO ?? session.IsCro ?? (sessionIsCro ? 1 : 0),
    );
    const mergedUserData = applySponsorCroLoginSession(
      stampPlatformSuperAdminSession(
        {
          ...userData,
          ...session,
          UserName: session.UserName || org.name,
          OrganizationId:
            session.OrganizationId || session.Organizationid || org.id,
          OrganizationName:
            session.OrganizationName || session.OrgName || org.name,
          StaffId: session.StaffId || org.staffId,
          Role: role,
          Sessionkey:
            session.Sessionkey || session.APIPWD || userData.Sessionkey,
          APIPWD: session.APIPWD || session.Sessionkey || userData.APIPWD,
          IsCRO: sessionIsCro ? croIsCroValue || 1 : 0,
          Permission: sessionIsCro
            ? ""
            : isPlatformOrSystemAdminRole(role)
              ? getAllPermissionKeys().join(",")
              : session.Permission != null
                ? session.Permission
                : userData.Permission || "",
          Modules: sessionIsCro ? [] : session.Modules ?? userData.Modules,
          GrantedModules:
            session.GrantedModules ?? session.grantedModules ?? [],
          ExternalEntityAccess:
            session.ExternalEntityAccess ??
            session.externalEntityAccess ??
            [],
          OtherOrgLink: session.OtherOrgLink || userData.OtherOrgLink,
          QmsTrainingAdditionalRolesEnabled:
            session.QmsTrainingAdditionalRolesEnabled ??
            session.qmsTrainingAdditionalRolesEnabled ??
            false,
          SponsorCroManagementEnabled:
            session.SponsorCroManagementEnabled ??
            session.sponsorCroManagementEnabled ??
            false,
          MfaEnabled: isOrgMfaRequired(
            session.MfaEnabled ?? session.mfaEnabled ?? org.mfaEnabled,
          ),
        },
        userData,
      ),
    );

    storeUserData(mergedUserData);
    clearOrgModulesHydrationAttempt();
    clearOrgPlatformFlagsHydrationAttempt();
    refreshUserData?.();

    setAuthItem("userName", mergedUserData.UserName || "");
    setAuthItem("userRole", mergedUserData.Role || "");
    setAuthItem("organizationName", mergedUserData.OrganizationName || "");

    setTimeout(() => {
      navigate(resolvePostLoginRoute(mergedUserData, false));
    }, 300);
  };

  const beginMfaForOrg = async (org) => {
    const challengeResponse = await startOrgEntryMfaChallenge({
      staffId: org.staffId,
      emailId: userData?.EmailId,
      organizationId: org.id,
      userName: userData?.UserName,
    });

    if (challengeResponse?.success === 1) {
      return null;
    }

    if (challengeResponse?.success === 2 && challengeResponse.data) {
      return challengeResponse.data;
    }

    throw new Error(
      challengeResponse?.message || "Could not start MFA for this organisation.",
    );
  };

  const switchToOrg = async (org, verifiedToken = "", ids = {}) => {
    const staffId = ids.staffId || org.staffId;
    const organizationId = ids.organizationId || org.id;
    const response = await changeOrganizationSession(userData, {
      organizationId,
      staffId,
      orgType: org.type,
      mfaVerifiedToken: verifiedToken,
    });

    const challengeToken =
      response?.data?.MfaSessionToken || response?.data?.mfaSessionToken;
    const isMfaChallenge =
      Number(response?.success) === 2 &&
      response?.data &&
      challengeToken &&
      !verifiedToken;

    if (isMfaChallenge) {
      setPendingOrg({ ...org, staffId, id: organizationId });
      setMfaChallenge(response.data);
      setMfaStep("mfaChoose");
      setMfaVerifiedToken("");
      return;
    }

    if (Number(response?.success) === 2 && !verifiedToken) {
      const challenge = await beginMfaForOrg({
        ...org,
        staffId,
        id: organizationId,
      });
      if (challenge) {
        setPendingOrg({ ...org, staffId, id: organizationId });
        setMfaChallenge(challenge);
        setMfaStep("mfaChoose");
        setMfaVerifiedToken("");
        return;
      }
    }

    if (Number(response?.success) !== 1 || !response?.data) {
      throw new Error(response?.message || "Could not switch organisation.");
    }

    applyOrgSession(response.data, org);
  };

  const handleOrgSelect = async (org) => {
    setSelectedOrg(org);
    setSwitchError("");
    setSwitchingOrgId(org.id);
    setMfaError("");

    try {
      const needsMfa =
        !isSessionSuperAdmin(userData) &&
        isOrgMfaRequired(org.mfaEnabled);

      if (needsMfa && !mfaVerifiedToken) {
        const challenge = await beginMfaForOrg(org);
        if (challenge) {
          setPendingOrg(org);
          setMfaChallenge(challenge);
          setMfaStep("mfaChoose");
          setSwitchingOrgId(null);
          return;
        }
      }

      await switchToOrg(org, mfaVerifiedToken);
      setMfaVerifiedToken("");
    } catch (error) {
      setSwitchError(error?.message || "Could not switch organisation.");
      setSwitchingOrgId(null);
    }
  };

  const handleMfaVerify = async (otpCode) => {
    if (!pendingOrg) return;
    setMfaLoading(true);
    setMfaError("");
    try {
      const token =
        mfaChallenge?.MfaSessionToken ?? mfaChallenge?.mfaSessionToken;
      const response = await verifyMfaLogin(token, otpCode);
      if (response.success !== 1) {
        setMfaError(response.message || "Invalid verification code.");
        return;
      }

      const verifiedToken =
        response?.data?.MfaSessionToken ||
        response?.data?.mfaSessionToken ||
        token;
      const verifiedStaffId =
        response?.data?.StaffId ||
        response?.data?.staffId ||
        pendingOrg.staffId;
      const verifiedOrganizationId =
        response?.data?.OrganizationId ||
        response?.data?.organizationId ||
        pendingOrg.id;

      setMfaChallenge(null);
      setMfaStep(null);
      setSwitchingOrgId(verifiedOrganizationId || pendingOrg.id);
      await switchToOrg(
        {
          ...pendingOrg,
          staffId: verifiedStaffId,
          id: verifiedOrganizationId,
        },
        verifiedToken,
        {
          staffId: verifiedStaffId,
          organizationId: verifiedOrganizationId,
        },
      );
      setPendingOrg(null);
      setMfaVerifiedToken("");
    } catch (error) {
      const message =
        error?.message || "Verification failed. Please try again.";
      setMfaError(message);
      setSwitchError(message);
    } finally {
      setMfaLoading(false);
      setSwitchingOrgId(null);
    }
  };

  const handleMfaBack = () => {
    setMfaChallenge(null);
    setMfaStep(null);
    setPendingOrg(null);
    setMfaError("");
    setSwitchingOrgId(null);
  };

  const getRoleBadgeClass = (role) => {
    const colors = {
      Admin: "badge bg-danger",
      Manager: "badge bg-primary",
      Instructor: "badge bg-success",
      Member: "badge bg-secondary",
    };
    return colors[role] || "badge bg-secondary";
  };

  if (loading) {
    return (
      <div className="org-loading-container">
        <div className="org-loading-content">
          <div className="org-loading-spinner"></div>
          <p className="org-loading-text">Loading your organizations...</p>
        </div>
      </div>
    );
  }

  if (mfaStep && mfaChallenge) {
    return (
      <div className="org-selector-container">
        <div className="org-transparent-header">
          <div className="org-header-logo">
            <img src="/logo.png" alt="Logo" className="org-header-logo-img" />
          </div>
          <div className="org-header-title-center">
            <h1 className="org-header-title-text">
              Verify to enter {pendingOrg?.name || "organisation"}
            </h1>
          </div>
          <div className="org-header-logout">
            <button className="org-logout-btn" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        </div>
        <div className="org-selector-wrapper" style={{ maxWidth: 480, margin: "2rem auto" }}>
          {mfaStep === "mfaChoose" ? (
            <MfaMethodChoice
              challenge={mfaChallenge}
              onSelected={(c) => {
                setMfaChallenge(c);
                setMfaStep("mfa");
                setMfaError("");
              }}
              onSetupRequired={(c) => {
                setMfaChallenge(c);
                setMfaStep("mfaAuthSetup");
                setMfaError("");
              }}
              onBack={handleMfaBack}
              loading={mfaLoading}
            />
          ) : null}
          {mfaStep === "mfaAuthSetup" ? (
            <MfaLoginAuthenticatorSetup
              challenge={mfaChallenge}
              onComplete={(c) => {
                setMfaChallenge(c);
                setMfaStep("mfa");
              }}
              onBack={() => setMfaStep("mfaChoose")}
            />
          ) : null}
          {mfaStep === "mfa" ? (
            <MfaVerification
              challenge={mfaChallenge}
              onVerify={handleMfaVerify}
              onBack={handleMfaBack}
              loading={mfaLoading}
              error={mfaError}
              onChallengeUpdate={setMfaChallenge}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="org-selector-container">
      <div className="org-transparent-header">
        <div className="org-header-logo">
          <img src="/logo.png" alt="Logo" className="org-header-logo-img" />
        </div>
        <div className="org-header-title-center">
          <h1 className="org-header-title-text">Select Organization</h1>
        </div>
        <div className="org-header-logout">
          <button className="org-logout-btn" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className="org-selector-wrapper">
        <div className="org-list-container">
          <div className="org-search-section">
            <div className="org-search-wrapper">
              <i className="fas fa-search org-search-icon"></i>
              <input
                type="text"
                className="form-control org-search-input"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {switchError ? (
            <p className="text-danger small px-3 mb-0" role="alert">
              {switchError}
            </p>
          ) : null}

          <div className="org-list-content">
            {filteredOrgs.length === 0 ? (
              <div className="org-empty-state">
                <div className="org-empty-icon">
                  <i className="fas fa-building"></i>
                </div>
                <p className="org-empty-text">No organizations found</p>
              </div>
            ) : (
              <div className="org-list-grid">
                {filteredOrgs.map((org) => (
                  <div
                    key={org.id}
                    className={`org-card ${
                      selectedOrg?.id === org.id ? "org-card-selected" : ""
                    }${switchingOrgId === org.id ? " org-card-switching" : ""}`}
                    onClick={() => {
                      if (switchingOrgId || mfaStep) return;
                      handleOrgSelect(org);
                    }}
                  >
                    <div className="org-card-content">
                      <div className="org-card-icon">
                        <i className="fas fa-building"></i>
                      </div>
                      <div className="org-card-info">
                        <h3 className="org-card-name">{org.name}</h3>
                        <div className="org-card-meta">
                          <span className={getRoleBadgeClass(org.role)}>
                            {org.role}
                          </span>
                          {org.mfaEnabled ? (
                            <span className="badge bg-warning text-dark ms-1">
                              MFA
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {switchingOrgId === org.id ? (
                        <div className="spinner-border spinner-border-sm" />
                      ) : (
                        <i className="fas fa-chevron-right org-card-arrow" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSelector;
